import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertCircle, ArrowLeft, Mail, CheckCircle2, Loader2 } from 'lucide-react';

// Passwordless CMS admin sign-in.
// Step 1: enter email → server emails a 6-digit code via Resend.
// Step 2: enter the code → session token issued, redirect into the CMS.
// After sending, a 60s "Resend code" cooldown timer is displayed. On expiry
// the button becomes clickable to request a fresh code.
export const AdminLogin = () => {
  const { requestOTP, verifyOTP } = useCMSAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('email');   // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef(null);

  // If the previous session ended because of idle timeout, surface a gentle
  // notice on the login screen instead of dumping the user without context.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('cms_last_logout_reason');
      if (reason === 'idle') {
        setInfo('You were signed out after 6 hours of inactivity. Enter your email to sign back in.');
        sessionStorage.removeItem('cms_last_logout_reason');
      }
    } catch { /* ignore */ }
  }, []);

  // Countdown for the Resend button
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Autofocus code input when we switch to step 2
  useEffect(() => {
    if (step === 'code') setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [step]);

  const sendCode = async () => {
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const data = await requestOTP(email.trim().toLowerCase());
      setCooldown(data?.cooldown_seconds ?? 60);
      setStep('code');
      setInfo(`We've emailed a 6-digit code to ${email}. It expires in ${data?.expires_in_minutes ?? 10} minutes.`);
    } catch (err) {
      setError(err.message || 'Could not send the code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0) return;
    setError('');
    setBusy(true);
    try {
      const data = await requestOTP(email.trim().toLowerCase());
      setCooldown(data?.cooldown_seconds ?? 60);
      setInfo(`New code sent to ${email}.`);
      setCode('');
    } catch (err) {
      setError(err.message || 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await verifyOTP(email.trim().toLowerCase(), code);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally {
      setBusy(false);
    }
  };

  // Numeric-only, 6-char clamp
  const onCodeChange = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 6);
    setCode(cleaned);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ux-charcoal via-ux-charcoal to-ux-violet-dark p-4"
      data-testid="admin-login-page"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-ux-cyan/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-ux-violet/20 rounded-full blur-3xl" />

      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-ux-cyan to-ux-violet flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">UniXperts CMS</CardTitle>
          <CardDescription>
            {step === 'email' ? 'Sign in with your admin email' : 'Enter the code we just emailed you'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 'email' ? (
            <form
              onSubmit={(e) => { e.preventDefault(); sendCode(); }}
              className="space-y-4"
              data-testid="otp-email-step"
            >
              {info && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2 text-blue-800 text-sm" data-testid="login-info">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{info}</span>
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-ux-charcoal">Email</label>
                <Input
                  type="email"
                  placeholder="admin@unixperts.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="email-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90"
                disabled={busy || !email}
                data-testid="send-code-btn"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending code…</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Email me a code</>
                )}
              </Button>

              <p className="text-xs text-ux-charcoal/60 text-center">
                No passwords — we&apos;ll email you a 6-digit code to sign in.
              </p>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4" data-testid="otp-code-step">
              {info && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2 text-emerald-800 text-sm" data-testid="login-info">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{info}</span>
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-ux-charcoal">6-digit code</label>
                <Input
                  ref={codeInputRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  maxLength={6}
                  className="text-center text-2xl font-mono tracking-[0.5em]"
                  data-testid="code-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90"
                disabled={busy || code.length !== 6}
                data-testid="verify-code-btn"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</>
                ) : (
                  'Verify & sign in'
                )}
              </Button>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => { setStep('email'); setCode(''); setError(''); setInfo(''); }}
                  className="text-ux-blue hover:underline flex items-center gap-1"
                  data-testid="change-email-btn"
                >
                  <ArrowLeft className="w-3 h-3" /> Change email
                </button>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={cooldown > 0 || busy}
                  className={`${cooldown > 0 ? 'text-ux-charcoal/40 cursor-not-allowed' : 'text-ux-blue hover:underline'}`}
                  data-testid="resend-code-btn"
                >
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 pt-6 border-t">
            <p className="text-xs text-ux-charcoal/60 text-center mb-3">Role-Based Access Control</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Super Admin</Badge>
              <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Approver</Badge>
              <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Editor</Badge>
              <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20">Viewer</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
